<template>
  <div>
    <el-row :gutter="20" v-if="invoiceData.invoice_id">
      <el-col :xs="24">
        <el-descriptions
          class="dialog-desc"
          labelClassName="dialog-desc-label"
          :column="2"
        >
          <el-descriptions-item
            :label="getLabelText(base, 'legal_entity_name')"
          >
            {{ invoiceData.legal_entity_name }}
          </el-descriptions-item>
          <el-descriptions-item
            :label="getLabelText(base, 'invoice_number')"
          >
            {{ invoiceData.number }}
          </el-descriptions-item>
          <el-descriptions-item
            :label="getLabelText(base, 'branch_name')"
          >
            {{ invoiceData.branch_name }}
          </el-descriptions-item>
          <el-descriptions-item
            :label="getLabelText(actionBase, 'decrease')"
          >
            <span
              v-html="decreaseWording"
            >
            </span>
          </el-descriptions-item>
          <el-descriptions-item
            :label="getLabelText(base, 'bid')"
          >
            {{ invoiceData.bid }}
          </el-descriptions-item>
          <el-descriptions-item
            :label="getLabelText(actionBase, 'increase')"
          >
            <span
              v-html="increaseWording"
            >
            </span>
          </el-descriptions-item>
        </el-descriptions>
      </el-col>
    </el-row>
    <el-card class="dialog-statistic">
      <el-row :gutter="20">
        <el-col :xs="12" :md="6">
          <el-statistic
            :title="transText(getActionBase('statistic.note'))"
            :value-style="{color: 'red'}"
          >
            <template slot="formatter">
              <span>
                {{ noteTypeText }}
              </span>
            </template>
          </el-statistic>
        </el-col>
        <el-col :xs="12" :md="6">
          <el-statistic
            :title="transText(getActionBase('statistic.ecvNote'))"
            :value-style="{color: 'red'}"
          >
            <template slot="formatter">
              <span>
                {{ ecvNoteTypeText }}
              </span>
            </template>
          </el-statistic>
        </el-col>
        <el-col :xs="12" :md="6">
          <el-statistic
            group-separator=","
            :precision="currencyPrecision"
            :value="coverFeeAdjTotal"
            :value-style="{color: 'red'}"
            :title="transText(getActionBase('statistic.coverFeeAdjTotal'))"
          >
            <template slot="prefix">
              <span class="number">
                {{ currencyCode }}
              </span>
            </template>
          </el-statistic>
        </el-col>
        <el-col :xs="12" :md="6">
          <el-statistic
            group-separator=","
            :precision="0"
            :value="coverAdjTotal"
            :value-style="{color: 'red'}"
            :title="transText(getActionBase('statistic.coverAdjTotal'))"
          >
          </el-statistic>
        </el-col>
      </el-row>
    </el-card>
    <el-row :gutter="20">
      <el-col :xs="24">
        <time-field
          v-model="formData.date"
          :base="base"
          name="date"
          prop="date"
          dataType="datetime"
          format="yyyy-MM-dd"
          valueFormat="yyyy-MM-dd"
          required
        >
        </time-field>
      </el-col>
      <el-col :xs="24" :md="12">
        <std-opt
          v-model="formData.due_date_type"
          name="documentDueDateType"
          component="radio-button"
          :hiddenValue="dueDateHiddenValue"
          :disabled="!isDebitNote"
        >
        </std-opt>
      </el-col>
      <el-col :xs="24" :md="12" class="due-date-field">
        <text-field
          v-model="dueDate"
          :base="base"
          name="due_date"
          disabled
        >
        </text-field>
      </el-col>
      <el-col :xs="24"
        v-if="showDueDateTypeAlert"
      >
        <el-alert
          :title="dueDateTypeAlertTitle"
          :closable="false"
          type="warning"
          show-icon
        >
        </el-alert>
      </el-col>
    </el-row>
    <el-tabs
      class="dialog-tabs"
      v-model="activeTab"
    >
      <el-tab-pane
        :label="getLabelText(getActionBase('tabs'), 'bookings')"
        name="bookings"
      >
        <el-row>
          <el-col :xs="24" :md="8">
            <el-input
              v-model="srchOrdersID"
              suffix-icon="el-icon-search"
              :placeholder="transText(getActionBase('tabs.bookings.search.placeholder'))"
              clearable
            >
            </el-input>
          </el-col>
          <el-table
            class="dialog-table"
            v-loading="loading"
            :data="list"
            :infinite-scroll-disabled="!listHasMore"
            v-el-table-infinite-scroll="loadMore"
            max-height="550"
            height="550px"
            row-key="orders_id"
          >
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'orders_id')"
              prop="orders_id"
              align="center"
              width="130"
              fixed="left"
            >
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'orders_num')"
              prop="orders_num"
              align="center"
              width="120"
            >
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'booking_time')"
              align="center"
              width="200"
            >
              <template slot-scope="scope">
                <span> {{ formatBookingTime(scope.row) }} </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'tier_discount')"
              align="center"
              width="80"
            >
              <template slot-scope="scope">
                <span> {{ scope.row.tier_discount }}% </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'cust_num')"
              prop="cust_num"
              align="center"
              width="80"
            >
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'attend_cover')"
              align="center"
              width="90"
            >
              <template slot-scope="scope">
                <span> {{ getAttendedCovers(scope.row) }} </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'attend_status')"
              align="center"
              width="100"
            >
              <template slot-scope="scope">
                <span
                  :style="getAttendStyle(scope.row.payment_set_attend_status, getAttendStatus(scope.row))"
                >
                  {{ getAttendChanges(scope.row.payment_set_attend_status, getAttendStatus(scope.row)) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              align="center"
              width="140"
              label="Attended"
            >
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.attended"
                  :min="0"
                  :max="99"
                  :step="1"
                  size="mini"
                  @change="onEdit(scope.row)"
                ></el-input-number>
              </template>
            </el-table-column>
            <el-table-column
              :label="getLabelText(getActionBase('tabs.bookings'), 'cover_adj')"
              align="center"
              width="100"
            >
              <template slot-scope="scope">
                <span
                  :style="(getCoverAdj(scope.row) !== 0) ? {color: 'red'} : {}"
                >
                  {{ getCoverAdj(scope.row) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </el-row>
      </el-tab-pane>
      <el-tab-pane
        name="editList"
      >
        <span slot="label">
          {{ getLabelText(getActionBase('tabs'), 'editList') }}
          <el-badge
            v-if="editList.length"
            :value="editList.length"
          >
          </el-badge>
        </span>
        <el-table
          class="dialog-table"
          :data="editList"
          max-height="400"
          row-key="orders_id"
        >
          <el-table-column type="expand">
            <template slot-scope="props">
              <el-form
                label-position="left"
                inline class="table-expand"
              >
                <el-form-item
                  :label="getLabelText(getActionBase('tabs.editList'), 'cover_adj')"
                >
                  <span>{{ props.row.cover_adj }}</span>
                </el-form-item>
                <el-form-item
                  :label="getLabelText(getActionBase('tabs.editList'), 'pax_unit_price')"
                >
                  <span>{{ currencyCode + ' ' + props.row.pax_unit_price }}</span>
                </el-form-item>
                <el-form-item
                  :label="getLabelText(getActionBase('tabs.editList'), 'pax_fee_adj')"
                >
                  <span>{{ currencyCode + ' ' + props.row.pax_fee_adj }}</span>
                </el-form-item>
              </el-form>
            </template>
          </el-table-column>
          <el-table-column
            :label="getLabelText(getActionBase('tabs.editList'), 'orders_id')"
            prop="orders_id"
            align="center"
          >
          </el-table-column>
          <el-table-column
            :label="getLabelText(getActionBase('tabs.editList'), 'orders_num')"
            prop="orders_num"
            align="center"
            width="140"
          >
          </el-table-column>
          <el-table-column
            :label="getLabelText(getActionBase('tabs.editList'), 'attend_status')"
            align="center"
            width="180"
          >
            <template slot-scope="scope">
              <span
                :style="getAttendStyle(scope.row.payment_set_attend_status, scope.row.new_attend_status)"
              >
                {{ getAttendChanges(scope.row.payment_set_attend_status, scope.row.new_attend_status) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="getLabelText(getActionBase('tabs.editList'), 'attended')"
            align="center"
          >
            <template slot-scope="scope">
              <span class="red">
                {{ getAttendedCovers(scope.row) + ' ➜ ' + scope.row.attended }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="getLabelText(getActionBase('tabs.editList'), 'pax_fee')"
            align="center"
          >
            <template slot-scope="scope">
              <span class="red">
                {{ currencyCode + ' ' + scope.row.payment_set_pax_fee + ' ➜ ' + scope.row.new_pax_fee }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import ElTableInfiniteScroll from 'el-table-infinite-scroll'
import SOA from '@/mixins/soa'
import {
  transText,
  getLabelText,
  getOptName
} from '@/utils/i18n'
import { fetchList } from '@/api/chart/statement_orders_set'
import {
  SOANoteType,
  PaymentAttendStatus
} from '@/enums'
import { debounce as _debounce } from 'lodash'
import { decimalFormat } from '@/utils/number'

const defListQuery = {
  page: 0,
  limit: 20,
  sort: '-id'
}

export default {
  name: 'cover-fees-tab',
  mixins: [SOA],
  directives: {
    'el-table-infinite-scroll': ElTableInfiniteScroll
  },
  data () {
    return {
      dueDate: '',
      editList: [],
      list: [],
      listPage: 0,
      listHasMore: true,
      srchOrdersID: null,
      activeTab: 'bookings',
      loading: false
    }
  },
  props: {
    base: {
      type: String,
      default: 'note'
    },
    actionBase: {
      type: String,
      default: 'actions.createNote.byEditBookings'
    },
    formData: {
      type: Object,
      required: true
    },
    invoiceData: {
      type: Object,
      required: true
    },
    branchData: {
      type: Object,
      required: true
    }
  },
  computed: {
    listQuery () {
      let listQuery = Object.assign({}, defListQuery)
      let filter = {
        '$and': [
          { statement_id: this.invoiceData.statement_id },
          { attend_status: { $ne: PaymentAttendStatus.Cancel } },
          { cust_num: { $gt: 0 } }
        ]
      }
      if (this.srchOrdersID) {
        filter['$and'].push({ orders_id: this.srchOrdersID })
      }
      listQuery.filter = JSON.stringify(filter)
      listQuery.page = this.listPage

      return listQuery
    },
    increaseCount () {
      return this.editList.filter(item => item.cover_adj > 0).length
    },
    increaseWording () {
      let count = (this.increaseCount > 0)
        ? `<span class="red">${this.increaseCount}</span>`
        : 0

      return this.transText(this.getActionBase('bookingsCount'), {
        count: count
      })
    },
    decreaseCount () {
      return this.editList.filter(item => item.cover_adj < 0).length
    },
    decreaseWording () {
      let count = (this.decreaseCount > 0)
        ? `<span class="red">${this.decreaseCount}</span>`
        : 0

      return this.transText(this.getActionBase('bookingsCount'), {
        count: count
      })
    },
    coverFeeAdjTotal () {
      return this.editList.reduce(
        (acc, item) => this.decimalFormat(acc + item.pax_fee_adj, this.currencyPrecision), 0)
    },
    coverAdjTotal () {
      return this.editList.reduce((acc, item) => acc + item.cover_adj, 0)
    },
    ecvAmountAdjTotal () {
      let total = 0

      this.editList.forEach(item => {
        if (item.payment_set_attend_status !== item.new_attend_status) {
          if (item.new_attend_status === PaymentAttendStatus.Attended) {
            total += item.ecv_amount
          } else {
            total -= item.ecv_amount
          }
          total = this.decimalFormat(total, this.currencyPrecision)
        }
      })

      return total
    },
    hasECVNote () {
      return this.editList.some(item =>
        item.payment_set_attend_status !== item.new_attend_status && item.ecv_amount !== 0
      )
    },
    noteType () {
      if (this.coverFeeAdjTotal > 0) {
        return SOANoteType.Debit
      }

      return SOANoteType.Credit
    },
    noteTypeText () {
      return this.getOptName('noteType', this.noteType)
    },
    isDebitNote () {
      return this.noteType === SOANoteType.Debit || this.ecvNoteType === SOANoteType.Debit
    },
    ecvNoteType () {
      if (this.ecvAmountAdjTotal < 0) {
        return SOANoteType.Debit
      } else if (this.ecvAmountAdjTotal > 0 || this.hasECVNote) {
        return SOANoteType.Credit
      }

      return null
    },
    ecvNoteTypeText () {
      if (this.ecvNoteType === null) {
        return 'None'
      }

      return this.getOptName('noteType', this.ecvNoteType)
    },
    tzLocation () {
      if (this.branchData && this.branchData.region && this.branchData.region.length > 0) {
        return this.branchData.region[0].tz_location
      }

      return ''
    }
  },
  methods: {
    decimalFormat,
    transText,
    getLabelText,
    getOptName,
    fetchList,
    reset () {
      this.resetList()
      this.srchOrdersID = null
      this.editList = []
      this.handleDueDateType()
    },
    resetList () {
      this.list = []
      this.listPage = 0
      this.listHasMore = true
    },
    getList () {
      this.loading = true
      this.fetchList(this.listQuery).then(res => {
        res.data.forEach(item => {
          item.attended = this.getAttendedCovers(item)
        })

        this.list = this.list.concat(res.data)
        if (this.list.length >= res.total_count) {
          this.listHasMore = false
        }

        this.applyEdits()
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.loading = false
      })
    },
    reloadList: _debounce(function () {
      this.resetList()
      this.loadMore()
    }, 1000),
    loadMore () {
      if (this.listHasMore && !this.loading) {
        this.listPage++
        this.getList()
      }
    },
    applyEdits () {
      this.editList.forEach(item => {
        let index = this.list.findIndex(row => {
          return row.id === item.id
        })
        if (index !== -1) {
          this.list[index].attended = item.attended
        }
      })
    },
    getActionBase (suffix) {
      return this.actionBase + '.' + suffix
    },
    getAttendedCovers (row) {
      return (row.payment_set_attend_status === 0) ? row.payment_set_cust_num : 0
    },
    getAttendStatus (row) {
      return (row.attended === 0) ? PaymentAttendStatus.NoShow : PaymentAttendStatus.Attended
    },
    getAttendStyle (oldStatus, newStatus) {
      if (oldStatus !== newStatus) {
        return {color: 'red'}
      }

      return {}
    },
    getAttendChanges (oldStatus, newStatus) {
      if (oldStatus !== newStatus) {
        return this.getOptName('attendStatus', oldStatus) +
          ' ➜ ' +
          this.getOptName('attendStatus', newStatus)
      }

      return this.getOptName('attendStatus', newStatus)
    },
    getCoverAdj (row) {
      return row.attended - this.getAttendedCovers(row)
    },
    formatBookingTime (row) {
      if (this.tzLocation) {
        return this.$moment.tz(row.booking_time, this.tzLocation).format('YYYY-MM-DD HH:mm:ss Z')
      }

      return row.booking_time
    },
    onEdit (row) {
      if (this.getCoverAdj(row) !== 0) {
        this.updateOrPushEditList(row)
      } else {
        this.editList = this.editList.filter(item => item.id !== row.id)
      }
    },
    updateOrPushEditList (row) {
      let index = this.editList.findIndex(item => item.id === row.id)
      let edit = {
        id: row.id,
        orders_id: row.orders_id,
        orders_num: row.orders_num,
        payment_set_cust_num: row.payment_set_cust_num,
        attended: row.attended,
        payment_set_attend_status: row.payment_set_attend_status,
        new_attend_status: (row.attended !== 0) ? PaymentAttendStatus.Attended : PaymentAttendStatus.NoShow,
        ecv_amount: row.ecv_amount,
        cover_adj: this.getCoverAdj(row),
        pax_unit_price: row.payment_set_pax_unit_price,
        payment_set_pax_fee: row.payment_set_pax_fee,
        new_pax_fee: this.decimalFormat(
          row.attended * row.payment_set_pax_unit_price, this.currencyPrecision),
        pax_fee_adj: this.decimalFormat(
          (row.attended * row.payment_set_pax_unit_price) - row.payment_set_pax_fee, this.currencyPrecision)
      }

      if (index === -1) {
        this.editList.push(edit)
      } else {
        this.editList.splice(index, 1, edit)
      }
    },
    handleDueDateType () {
      let type = SOANoteType.Credit
      if (this.isDebitNote) {
        type = SOANoteType.Debit
      }

      this.$_handleDueDateType(type)
      this.$_calcDueDate()
    }
  },
  mounted () {
    this.handleDueDateType(this.noteType)
  },
  watch: {
    'editList': {
      handler (editList) {
        this.$emit('change', {
          editList: editList
        })
      },
      deep: true
    },
    'srchOrdersID' (v) {
      this.reloadList()
    },
    'noteType' (v) {
      this.handleDueDateType()
    },
    'ecvNoteType' (v) {
      this.handleDueDateType()
    },
    'formData.date' (v) {
      this.$_calcDueDate()
    },
    'formData.due_date_type' (v) {
      this.$_calcDueDate()
    }
  }
}
</script>
